import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, FlaskConical } from 'lucide-react';
import { ApiError, completeMockPayment, qk, type MockOutcome } from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Card from '../components/Card.js';
import { BILLING_PATH, formatKurus } from './Billing.js';

/**
 * §45.1/§45.5 — MOCK ödeme sayfası. Gerçek sağlayıcının checkout ekranının yerini
 * tutar ve YALNIZ `PAYMENT_PROVIDER=mock` kurulumunda anlamlıdır (gerçek sağlayıcıda
 * uç 404 döner, §45.5 notu 2).
 *
 * SAYFA GERÇEK SANILMAMALI: başlık, rozet ve uyarı şeridi bunun bir SİMÜLASYON
 * olduğunu açıkça söyler. Kart bilgisi İSTENMEZ — istenseydi kullanıcı gerçek kart
 * numarasını yazabilirdi ve o veri (§4 PII sınırı) hiçbir yerde bulunmamalıdır.
 *
 * Sayfa hiçbir karar VERMEZ: URL'deki imzalı bağlamı (`?ref&org&plan&sig`) olduğu gibi
 * `POST /api/billing/mock/complete`'e taşır. İmza `ref|org|plan` üçlüsünün kanıtıdır;
 * paneldeki bir değişiklik onu bozar ve sunucu 400 döner — yani bu ekran, kurcalanarak
 * bedava plan yükseltmenin yolu DEĞİLDİR.
 *
 * Guard'ın İÇİNDEDİR (AuthGate + Layout): kullanıcı paneli terk etmeden ödemesini
 * bitirir ve dönüşte `/faturalama` sayfası zaten açılabilir durumdadır.
 */

/** §45.1 — sunucudaki `MOCK_CHECKOUT_PATH` ile AYNI olmak ZORUNDA (checkout URL'ini o üretir). */
export const MOCK_CHECKOUT_PATH = '/odeme-simulasyonu';

/**
 * §45.5 — sayfanın üç düğmesi, sağlayıcının üç sonucunun karşılığıdır. Etiketler
 * kullanıcının GÖRECEĞİ sonucu anlatır; teknik olay adları (`subscription.activated`…)
 * sunucunun sözlüğüdür ve panelde yazılmaz.
 */
const SONUCLAR: { outcome: MockOutcome; label: string; testId: string; primary: boolean; help: string }[] = [
  {
    outcome: 'paid',
    label: 'Öde',
    testId: 'odeme-ode',
    primary: true,
    help: 'Abonelik aktifleşir ve organizasyon seçilen plana geçer.',
  },
  {
    outcome: 'failed',
    label: 'Ödeme başarısız',
    testId: 'odeme-basarisiz',
    primary: false,
    // §45.3 yumuşak yaptırım — bu düğme "hesap kapanır" demek DEĞİLDİR.
    help: 'Abonelik "ödeme gecikti" durumuna düşer; plan değişmez, özellikler kısılmaz.',
  },
  {
    outcome: 'exhausted',
    label: 'Denemeler tükendi',
    testId: 'odeme-tukendi',
    primary: false,
    help: 'Abonelik iptal edilir ve organizasyon ücretsiz plana iner; veriler silinmez.',
  },
];

/** §45.5 — uç sözleşmesi: kod + durumdan okunur, mesaj metninden değil. */
function odemeHataMetni(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return 'Bu kurulumda ödeme simülasyonu kapalı (gerçek sağlayıcı yapılandırılmış). Ödeme sağlayıcısının kendi sayfasından devam edin.';
    }
    if (error.code === 'invalid_signature') {
      return 'Bağlantıdaki imza doğrulanamadı — adres kurcalanmış ya da eksik kopyalanmış olabilir. Faturalama sayfasından yeniden başlatın.';
    }
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export default function MockCheckout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /*
   * Dördü de AYNEN taşınır: `sig`, diğer üçünün imzasıdır. Boşluk kırpmak/küçük harfe
   * indirmek bile imzayı bozar, o yüzden hiçbir "düzeltme" yapılmaz.
   */
  const ref = params.get('ref') ?? '';
  const org = params.get('org') ?? '';
  const plan = params.get('plan') ?? '';
  const sig = params.get('sig') ?? '';
  /** Tutar SUNUCUDA plan kaydından okunur; buradaki değer YALNIZ gösterimdir (§45.5). */
  const tutarKurus = Number(params.get('amount') ?? Number.NaN);
  const paraBirimi = params.get('currency') ?? 'TRY';

  const baglamTam = ref !== '' && org !== '' && plan !== '' && sig !== '';

  const tamamla = useMutation({
    mutationFn: (outcome: MockOutcome) => completeMockPayment({ ref, org, plan, sig, outcome }),
    onSuccess: async (data) => {
      /*
       * Sunucu olayı UYGULAMADIYSA (`applied:false`) faturalamaya dönmek yanıltıcı
       * olurdu: kullanıcı bir şey olduğunu sanır, sayfada hiçbir değişiklik görmez.
       * O durumda burada kalınır ve sebep yazılır (ör. henüz aboneliği olmayan bir
       * org'da "ödeme başarısız" sonucu uygulanacak bir satır bulamaz).
       */
      if (!data.applied) return;

      // Plan/kota da değişti: banner ve faturalama sayfası taze veriyle açılmalı.
      await queryClient.invalidateQueries({ queryKey: qk.orgSubscription(org) });
      await queryClient.invalidateQueries({ queryKey: qk.orgUsage(org) });
      navigate(BILLING_PATH, { replace: true });
    },
  });

  const hata = odemeHataMetni(tamamla.error);
  const uygulanmadi = tamamla.isSuccess && !tamamla.data.applied;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="odeme-simulasyonu-sayfasi">
      {/* başlık — SİMÜLASYON olduğu BURADA yazar; kullanıcı gerçek ödeme sanmamalı */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-brand" aria-hidden="true" />
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">
            Ödeme simülasyonu — gerçek ödeme değildir
          </h1>
          <BilgiDugmesi testId="odeme-simulasyonu-bilgi">Bu ekran GERÇEK ödeme almaz: plan yükseltme akışını uçtan uca denemek için mock ödeme sağlayıcısıdır (§45.1). Buradaki "ödeme" onaylandığında abonelik webhook'u tetiklenir ve Faturalama sayfası güncellenir.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Mock ödeme sağlayıcısı (§45.1) · test amaçlıdır
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-[560px] space-y-6 px-6 py-8">
        {/* Uyarı şeridi: renk tek taşıyıcı değil, metin de aynı şeyi söyler. */}
        <div
          data-testid="simulasyon-uyarisi"
          role="note"
          className="flex items-start gap-2 rounded-xl border border-[#fde68a] bg-[#fffbeb] px-4 py-3 text-[12px] text-[#92400e]"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Bu sayfa bir <span className="font-semibold">simülasyondur</span>: gerçek bir ödeme
            sağlayıcısına bağlı değildir, kart bilgisi istemez ve hiçbir tahsilat yapmaz.
            Aşağıdaki düğmeler yalnızca sağlayıcının göndereceği bildirimi taklit eder.
          </span>
        </div>

        <Card
          title="Ödeme sonucunu seçin"
          subtitle="Gerçek sağlayıcıda bu adımda kart bilgisi girilir; burada sonucu siz seçersiniz."
        >
          {!baglamTam ? (
            /*
             * Bağlam eksikse düğme GÖSTERİLMEZ: imzasız bir istek sunucudan kesin 400
             * alır ve kullanıcıya "bir şeyler ters gitti" demekten başka bir şey söylemez.
             */
            <div data-testid="odeme-baglam-eksik" className="space-y-3">
              <p className="text-[13px] text-[#374151]">
                Ödeme bağlamı eksik. Bu sayfa yalnız faturalama sayfasından başlatılan bir
                ödeme akışıyla açılır (adres <span className="font-mono">?ref&amp;org&amp;plan&amp;sig</span>{' '}
                taşımalıdır).
              </p>
              <button
                type="button"
                className="btn-primary"
                onClick={() => navigate(BILLING_PATH, { replace: true })}
              >
                Faturalamaya dön
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
                <dt className="text-[#9ca3af]">Plan</dt>
                <dd className="font-medium text-[#111827]" data-testid="odeme-plan">
                  {plan}
                </dd>
                <dt className="text-[#9ca3af]">Organizasyon</dt>
                <dd className="truncate font-mono text-[#374151]">{org}</dd>
                {Number.isFinite(tutarKurus) ? (
                  <>
                    <dt className="text-[#9ca3af]">Tutar</dt>
                    <dd className="num text-[#374151]">{formatKurus(tutarKurus, paraBirimi)}</dd>
                  </>
                ) : null}
                <dt className="text-[#9ca3af]">Ödeme kaydı</dt>
                <dd className="truncate font-mono text-[#9ca3af]">{ref}</dd>
              </dl>

              <div className="flex flex-col gap-2">
                {SONUCLAR.map((sonuc) => (
                  <div key={sonuc.outcome} className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      data-testid={sonuc.testId}
                      data-outcome={sonuc.outcome}
                      className={`${sonuc.primary ? 'btn-primary' : 'btn-ghost'} min-w-[168px] justify-center`}
                      disabled={tamamla.isPending}
                      onClick={() => tamamla.mutate(sonuc.outcome)}
                    >
                      {tamamla.isPending && tamamla.variables === sonuc.outcome
                        ? 'İşleniyor…'
                        : sonuc.label}
                    </button>
                    <span className="text-[12px] text-[#6b7280]">{sonuc.help}</span>
                  </div>
                ))}
              </div>

              {hata !== null ? (
                <p role="alert" data-testid="odeme-hata" className="text-[12px] text-red-600">
                  {hata}
                </p>
              ) : null}

              {uygulanmadi ? (
                <p data-testid="odeme-uygulanmadi" className="text-[12px] text-[#6b7280]">
                  Sağlayıcı bildirimi alındı ama uygulanacak bir abonelik bulunamadı — hiçbir
                  şey değişmedi. Önce bir ödeme tamamlayın (Öde), sonra diğer sonuçları
                  deneyin.
                </p>
              ) : null}

              <button
                type="button"
                data-testid="odeme-vazgec"
                className="btn-ghost"
                onClick={() => navigate(BILLING_PATH, { replace: true })}
              >
                Vazgeç
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
